import React from 'react';
import { Box, Typography, Skeleton, Fade, alpha } from '@mui/material';
import {
  TrendingUp,
  AutoAwesome,
  Assessment,
  ShowChart,
  Timeline,
  Bolt,
  Diamond,
  Balance,
  SwapHoriz,
  Inventory,
  BarChart,
  Stars,
  Layers,
  RocketLaunch,
  CandlestickChart,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const STYLE_LABEL_KEYS = {
  FLOW_LEADERS: 'vnstock.recommendations.styles.flowLeaders',
  QUALITY_GROWTH: 'vnstock.recommendations.styles.qualityGrowth',
  VALUE_WITH_CONFIRMATION: 'vnstock.recommendations.styles.valueConfirmation',
  TURNAROUND: 'vnstock.recommendations.styles.turnaround',
  ACCUMULATION_BREAKOUT: 'vnstock.recommendations.styles.accumulationBreakout',
  REVERSAL: 'vnstock.recommendations.styles.reversal',
  SUPER_STOCK: 'vnstock.recommendations.styles.superStock',
  PRICE_ACTION: 'vnstock.recommendations.styles.priceAction',
};

const STYLE_CONFIG = {
  FLOW_LEADERS:               { color: '#667eea', Icon: TrendingUp },
  QUALITY_GROWTH:             { color: '#10b981', Icon: AutoAwesome },
  VALUE_WITH_CATALYST:        { label: 'Value Catalyst',  color: '#f59e0b', Icon: ShowChart },
  VALUE_WITH_CONFIRMATION:    { color: '#8b5cf6', Icon: Assessment },
  MOMENTUM_BREAKOUT:          { label: 'Momentum',        color: '#ec4899', Icon: Bolt },
  GROWTH_AT_REASONABLE_PRICE: { label: 'GARP',            color: '#06b6d4', Icon: Balance },
  DEEP_VALUE:                 { label: 'Deep Value',      color: '#14b8a6', Icon: Diamond },
  TURNAROUND:                 { color: '#f97316', Icon: SwapHoriz },
  ACCUMULATION:               { label: 'Accumulation',    color: '#6366f1', Icon: Inventory },
  ACCUMULATION_BREAKOUT:      { color: '#7c3aed', Icon: Timeline },
  REVERSAL:                   { color: '#e11d48', Icon: SwapHoriz },
  SUPER_STOCK:                { color: '#06b6d4', Icon: RocketLaunch },
  PRICE_ACTION:               { color: '#e97320', Icon: CandlestickChart },
};

const RecommendationsStatsCards = ({
  recommendations = {},
  stats,
  isLoading,
  highConvictionStocks = []
}) => {
  const { t } = useTranslation();
  if (!stats && !isLoading) return null;

  const styleDistribution = Object.entries(recommendations || {}).map(([style, data]) => ({
    style,
    count: data?.results?.length || 0,
    ...(STYLE_CONFIG[style] || { color: '#64748b', Icon: BarChart }),
    label: t(STYLE_LABEL_KEYS[style] || style, style),
  })).filter(s => s.count > 0).sort((a, b) => b.count - a.count);

  const totalStocks = stats?.totalStocks || 0;
  const maxCount = Math.max(...styleDistribution.map(s => s.count), 1);
  const convictionRate = totalStocks > 0 ? Math.round((highConvictionStocks.length / totalStocks) * 100) : 0;

  return (
    <Fade in timeout={400}>
      <Box sx={{ mb: 2 }}>
        {/* ═══ Summary line ═══ */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Layers sx={{ fontSize: 16, color: '#667eea' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
              {isLoading ? '—' : t('vnstock.recommendations.stats.stockSummary', {
                count: totalStocks,
                defaultValue: `${totalStocks} stocks`,
              })}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              {t('vnstock.recommendations.stats.styleSummary', {
                count: stats?.totalStyles || 0,
                defaultValue: `from ${stats?.totalStyles || 0} styles`,
              })}
            </Typography>
          </Box>
          <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Stars sx={{ fontSize: 16, color: '#8b5cf6' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#8b5cf6' }}>
              {t('vnstock.recommendations.stats.highConvictionSummary', {
                count: highConvictionStocks.length,
                defaultValue: `${highConvictionStocks.length} High Conviction`,
              })}
            </Typography>
            <Typography sx={{
              fontSize: '0.65rem', fontWeight: 700,
              px: 0.6, py: 0.1, borderRadius: 0.75,
              bgcolor: alpha('#8b5cf6', 0.1), color: '#7c3aed',
            }}>
              {convictionRate}%
            </Typography>
          </Box>
        </Box>

        {/* ═══ Style tiles grid ═══ */}
        {!isLoading && styleDistribution.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {styleDistribution.map(item => {
              const IconComp = item.Icon;
              const pct = (item.count / maxCount) * 100;
              return (
                <Box key={item.style} sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  border: `1px solid ${alpha(item.color, 0.15)}`,
                  bgcolor: '#fff',
                  p: '10px 12px',
                  transition: 'all 0.2s',
                  cursor: 'default',
                  '&:hover': {
                    borderColor: alpha(item.color, 0.35),
                    boxShadow: `0 2px 12px ${alpha(item.color, 0.12)}`,
                    transform: 'translateY(-1px)',
                  }
                }}>
                  {/* Background bar */}
                  <Box sx={{
                    position: 'absolute', left: 0, bottom: 0, top: 0,
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${alpha(item.color, 0.06)} 0%, ${alpha(item.color, 0.02)} 100%)`,
                    transition: 'width 0.5s ease',
                  }} />

                  {/* Content */}
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 30, height: 30, borderRadius: 1.5,
                      background: `linear-gradient(135deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <IconComp sx={{ fontSize: 16, color: '#fff' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: '0.68rem', fontWeight: 700, color: '#1e293b',
                        lineHeight: 1.2, whiteSpace: 'nowrap',
                      }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1.2 }}>
                        {t('vnstock.recommendations.stats.stockSummary', {
                          count: item.count,
                          defaultValue: `${item.count} stocks`,
                        })}
                      </Typography>
                    </Box>
                    <Typography sx={{
                      fontSize: '1.15rem', fontWeight: 900, color: item.color,
                      lineHeight: 1, fontFamily: 'monospace',
                    }}>
                      {item.count}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: 2 }} />)}
          </Box>
        )}
      </Box>
    </Fade>
  );
};

export default RecommendationsStatsCards;
